import { Plus, Minus } from 'lucide-react';
import { useLanguage } from '@/contexts/AppContext';
import { a11yTranslations } from '@/i18n/a11y';

interface MenuQuantityControlProps {
  quantity: number;
  minQuantity?: number;
  maxQuantity?: number;
  onQuantityChange: (newQuantity: number) => void;
  label?: string;
}

export function MenuQuantityControl({
  quantity,
  minQuantity = 1,
  maxQuantity = 10,
  onQuantityChange,
  label
}: MenuQuantityControlProps) {
  const { locale } = useLanguage();
  const a11y = a11yTranslations[locale];

  const handleDecrease = () => {
    if (quantity > minQuantity) {
      onQuantityChange(quantity - 1);
    }
  };

  const handleIncrease = () => {
    if (quantity < maxQuantity) {
      onQuantityChange(quantity + 1);
    }
  };

  return (
    <div className="flex items-center justify-between font-['Noto_Sans:Medium',_'Noto_Sans_KR:Medium',_sans-serif]">
      {label && (
        <span
          className="text-[clamp(14px,3.5vw,16px)] text-foreground"
          style={{ fontFamily: "'Noto Sans KR', 'Noto Sans', sans-serif", fontWeight: 500 }}
        >
          {label}
        </span>
      )}
      <div className="flex items-center gap-[clamp(8px,2vw,10px)]">
        <button
          onClick={handleDecrease}
          disabled={quantity <= minQuantity}
          className="w-[clamp(30px,7.6vw,38px)] h-[clamp(30px,7.6vw,38px)] rounded-full bg-secondary flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-secondary/80 transition-all duration-200 shadow-sm"
          aria-label={a11y.decreaseQuantity}
        >
          <Minus className="w-[clamp(13px,3.25vw,16px)] h-[clamp(13px,3.25vw,16px)] text-secondary-foreground" />
        </button>

        <span className="min-w-[clamp(28px,7vw,32px)] text-center text-[clamp(14px,3.5vw,16px)] font-semibold text-foreground">
          {quantity.toString().padStart(2, '0')}
        </span>

        <button
          onClick={handleIncrease}
          disabled={quantity >= maxQuantity}
          className="w-[clamp(30px,7.6vw,38px)] h-[clamp(30px,7.6vw,38px)] rounded-full bg-secondary flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-secondary/80 transition-all duration-200 shadow-sm"
          aria-label={a11y.increaseQuantity}
        >
          <Plus className="w-[clamp(13px,3.25vw,16px)] h-[clamp(13px,3.25vw,16px)] text-secondary-foreground" />
        </button>
      </div>
    </div>
  );
}
